import { ChangeEvent } from "react";
import TextField from "@mui/material/TextField";
import { SxProps, Theme } from "@mui/material/styles";

/** Widest scale the amount columns behind this field store: DECIMAL(12,4). */
const DEFAULT_MAX_FRACTION_DIGITS = 4;

// Accepts only a non-negative decimal within the given scale (or empty while typing).
const isMoneyInput = (raw: string, maxFractionDigits: number): boolean =>
  raw === "" ||
  new RegExp(`^\\d*\\.?\\d{0,${maxFractionDigits}}$`).test(raw);

interface AmountInputProps {
  label?: string;
  value: string;
  error?: string;
  disabled?: boolean;
  /** Decimal places the column behind the field keeps; typing past it is ignored. */
  maxFractionDigits?: number;
  /** Shown while the field is empty; a table cell uses it for the value an empty field falls back to. */
  placeholder?: string;
  /** Names the field for assistive technology where no visible label fits, e.g. a table cell. */
  ariaLabel?: string;
  sx?: SxProps<Theme>;
  onChange: (value: string) => void;
}

/**
 * Money field kept as a string draft. It is deliberately `type="text"` with a
 * decimal input mode: a number input silently drops trailing separators while
 * typing and lets a scroll wheel change the amount.
 */
export default function AmountInput({
  label,
  value,
  error,
  disabled,
  maxFractionDigits = DEFAULT_MAX_FRACTION_DIGITS,
  placeholder = "0",
  ariaLabel,
  sx,
  onChange,
}: AmountInputProps) {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    if (isMoneyInput(next, maxFractionDigits)) onChange(next);
  };

  return (
    <TextField
      label={label}
      value={value}
      onChange={handleChange}
      type="text"
      placeholder={placeholder}
      size="small"
      fullWidth
      disabled={disabled}
      error={Boolean(error)}
      helperText={error}
      sx={sx}
      slotProps={{
        htmlInput: { inputMode: "decimal", "aria-label": ariaLabel },
        // Keep the label floated so the "0" placeholder shows without overlap.
        inputLabel: { shrink: true },
      }}
    />
  );
}
